import { goldPriceSeries } from '@/mocks/marketSeries';
import { journalEntries } from '@/utils/journalFeed';
import StepSection from './StepSection';

// The monitored position is read off the newest entry in the trade record, so
// this "open position" panel always reflects the latest recorded trade instead
// of a fixed price. The floating P&L is still computed live from the price series.
const POINT_VALUE = 100;

const trade = journalEntries[0];
const openEntry = Number(trade.entry);
const openSide = trade.side === 'BUY' ? 'LONG' : 'SHORT';
const openVolume = Number(trade.volume);
const dir = trade.side === 'BUY' ? 1 : -1;

const lastPrice = goldPriceSeries[goldPriceSeries.length - 1]?.price ?? openEntry;
const move = lastPrice - openEntry;
const floatingPnl = Number((move * dir * openVolume * POINT_VALUE).toFixed(2));

const rules = [
  {
    index: '01',
    label: 'Trailing Stop',
    detail: 'Advances behind the price as it moves',
    value: 'ARMED',
    tone: 'gold' as const,
  },
  {
    index: '02',
    label: 'Protective Stop',
    detail: 'Hard floor on the open position',
    value: 'SET',
    tone: 'default' as const,
  },
  {
    index: '03',
    label: 'Take Profit',
    detail: 'Target exit level while open',
    value: 'PENDING',
    tone: 'default' as const,
  },
  {
    index: '04',
    label: 'Time Exit',
    detail: 'Maximum holding window',
    value: 'STANDBY',
    tone: 'default' as const,
  },
];

function StepManage() {
  return (
    <StepSection
      id="step-07"
      index="07"
      stage="execute"
      title="Manage The Position"
      subtitle="Once a position is open, the trade moves into the management stage."
      body="The position remains subject to the EA's configured management and exit rules until the trade is closed — no manual intervention is required."
      band
    >
      <div className="rounded-lg border border-background-300 bg-background-50 overflow-hidden">
        <div className="flex items-center justify-between px-4 md:px-5 h-11 border-b border-background-200 bg-background-100">
          <span className="flex items-center gap-2 text-[11px] font-data tracking-widest text-foreground-700">
            <span className="w-1.5 h-1.5 rounded-full bg-primary-500 breathe"></span>
            POSITION OPEN · MONITORING
          </span>
          <span className="text-[10px] font-data tracking-widest text-foreground-400">HELD</span>
        </div>

        <div className="flex items-end justify-between px-4 md:px-5 pt-4 pb-3">
          <div>
            <p className="text-[10px] font-data tracking-[0.22em] text-foreground-400">
              FLOATING P&amp;L
            </p>
            <p
              className={`mt-1.5 font-data text-[30px] md:text-[34px] font-semibold leading-none ${
                floatingPnl >= 0 ? 'text-profit-600' : 'text-loss-600'
              }`}
            >
              {floatingPnl >= 0 ? '+' : '-'}
              {Math.abs(floatingPnl).toFixed(2)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[10px] font-data tracking-[0.22em] text-foreground-400">
              OPEN MOVE
            </p>
            <p
              className={`mt-1.5 font-data text-[15px] font-semibold leading-none ${
                move >= 0 ? 'text-profit-600' : 'text-loss-600'
              }`}
            >
              {move >= 0 ? '+' : ''}
              {move.toFixed(1)} PTS
            </p>
          </div>
        </div>

        <div className="divide-y divide-background-200 border-t border-background-200">
          {rules.map((row) => (
            <div key={row.index} className="flex items-center gap-3 px-4 md:px-5 py-3.5">
              <span className="w-5 shrink-0 font-data text-[11px] text-foreground-400">
                {row.index}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-semibold text-foreground-900 truncate">{row.label}</p>
                <p className="text-[11px] text-foreground-500 truncate">{row.detail}</p>
              </div>
              <span
                className={`inline-flex shrink-0 items-center gap-1.5 font-data text-[11px] tracking-wider ${
                  row.tone === 'gold' ? 'text-primary-700' : 'text-foreground-600'
                }`}
              >
                {row.tone === 'gold' && (
                  <span className="w-1.5 h-1.5 rounded-full bg-primary-500 breathe"></span>
                )}
                {row.value}
              </span>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between border-t border-background-200 bg-background-100 px-4 md:px-5 py-3">
          <span className="font-data text-[10px] tracking-widest text-foreground-400">
            XAUUSD · {openSide} {openVolume.toFixed(2)}
          </span>
          <span className="font-data text-[10px] tracking-widest text-foreground-500">
            RULES ACTIVE
          </span>
        </div>
      </div>
    </StepSection>
  );
}

export default StepManage;